import { Model, modelRegistry } from './Model';
import { TaxRate, ProductCategory, ProductIngredient } from '../models';
import { PrintRoleType } from '../types';
import ApiService from '../services/ApiService';
import Money from '../utilities/Money';
import Store from '../wrappers/Store';
import { I18n } from '../wrappers/I18n';
import moment from 'moment-timezone';
export class Product extends Model {
    static entity = 'products';
    static get PRINTING_SECTORS() {
        return [
            PrintRoleType.bar,
            PrintRoleType.kitchen,
            PrintRoleType.sector_1,
            PrintRoleType.sector_2
        ];
    }
    id = null;
    name = '';
    code = null;
    barcode = null;
    gtin = null;
    is_goods = true;
    is_measured = false;
    is_made = false;
    is_sold = true;
    excluded_from_delivery = false;
    description = null;
    image_url = null;
    dietary_tags = null;
    alcohol_percentage = null;
    requires_quantity = false;
    category_id = null;
    type = null;
    printing_sector = null;
    selling_price = '0.0000';
    purchase_price = null;
    tax_rate_id = null;
    count = '0.000';
    min_count = '0.000';
    selling_count = '0.000';
    unit_of_measure = null;
    unit_of_measure_sold = null;
    net_quantity = '0.000';
    modified_at = null;
    created_at = null;
    updated_at = null;
    prices = [];
    category;
    taxRate;
    price_difference = null;
    ingredients;
    static fields() {
        return {
            id: this.attr(null),
            name: this.attr(''),
            code: this.attr(null),
            barcode: this.attr(null),
            gtin: this.attr(null),
            is_goods: this.attr(true),
            is_measured: this.attr(false, value => !!value),
            is_made: this.attr(false, value => !!value),
            is_sold: this.attr(true, value => !!value),
            excluded_from_delivery: this.attr(false, value => !!value),
            description: this.attr(null),
            image_url: this.attr(null),
            dietary_tags: this.attr(null),
            alcohol_percentage: this.attr(null, value => (value === null || value === '' ? null : Number(value))),
            requires_quantity: this.attr(false, value => !!value),
            category_id: this.attr(null),
            type: this.attr(null),
            printing_sector: this.attr(null),
            selling_price: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            tax_rate_id: this.attr(null),
            count: this.attr('0.000', value => this.round(value, 3).toFixed(3)),
            min_count: this.attr(null, value => (value === null ? null : this.round(value, 3).toFixed(3))),
            selling_count: this.attr('0.000', value => this.round(value, 3).toFixed(3)),
            purchase_price: this.attr(null, value => value ? this.round(value, 4).toFixed(4) : null),
            unit_of_measure: this.attr(null),
            unit_of_measure_sold: this.attr(null),
            net_quantity: this.attr('0.000', value => value ? this.round(value, 3).toFixed(3) : null),
            modified_at: this.attr(null),
            created_at: this.attr(null),
            updated_at: this.attr(null),
            prices: this.attr([], (value) => {
                return value.map(item => {
                    return { id: item.id, name: item.name, price: this.round(item.price, 4).toFixed(4) };
                });
            }),
            category: this.attr(undefined, value => value ? new ProductCategory(value) : undefined),
            taxRate: this.attr(undefined, value => value ? new TaxRate(value) : undefined),
            price_difference: this.attr(null, value => value ? this.round(value, 4).toFixed(4) : null),
        };
    }
    constructor(record) {
        super();
        this.$fill(record);
    }
    /**
     * Fetch new products from server
     */
    static async fetchLatest() {
        const lastUpdatedAt = Product.getLastUpdatedAt();
        const params = { last_updated_at: lastUpdatedAt ? lastUpdatedAt.utc().format('YYYY-MM-DD HH:mm:ss') : null };
        const response = await ApiService.get('/products/latest', { params: params });
        Product.insert({ data: response.data.products });
    }
    /**
     * Make a copy of the Product.
     */
    $duplicate() {
        return new Product(this.$toJson());
    }
    getTaxRate() {
        return TaxRate.find(this.tax_rate_id);
    }
    getIngredients() {
        return ProductIngredient.all().filter(pi => pi.super_product_id === this.id);
    }
    getCount() {
        return parseFloat(this.count);
    }
    getSellingCount() {
        return parseFloat(this.selling_count);
    }
    getSellingPrice(pricelistId = 0) {
        if (pricelistId) {
            const pricelist = this.prices.find(pricelist => pricelist.id === pricelistId);
            if (pricelist) {
                return Money.parse(pricelist.price);
            }
        }
        return Money.parse(this.selling_price);
    }
    getPurchasePrice() {
        if (!this.purchase_price)
            return null;
        return Money.parse(this.purchase_price);
    }
    getPurchasePriceBeforeTax() {
        const purchasePrice = this.getPurchasePrice();
        if (!purchasePrice)
            return null;
        const taxRate = this.getTaxRate();
        if (!taxRate) {
            return purchasePrice;
        }
        const reverseCalculation = !!Store.get('tenant')?.venue?.settings.reverse_calculation;
        if (reverseCalculation) {
            return purchasePrice;
        }
        return purchasePrice.divide(1 + taxRate.getRate() / 100);
    }
    getPurchasePriceAfterTax() {
        const purchasePrice = this.getPurchasePrice();
        if (!purchasePrice)
            return null;
        const taxRate = this.getTaxRate();
        if (!taxRate) {
            return purchasePrice;
        }
        const reverseCalculation = !!Store.get('tenant')?.venue?.settings.reverse_calculation;
        if (reverseCalculation) {
            return purchasePrice.multiply(1 + taxRate.getRate() / 100);
        }
        return purchasePrice;
    }
    getPurchaseValueBeforeTax() {
        const priceBeforeTax = this.getPurchasePriceBeforeTax();
        if (!priceBeforeTax)
            return null;
        return priceBeforeTax.multiply(this.getCount());
    }
    getPurchaseValueAfterTax() {
        const priceAfterTax = this.getPurchasePriceAfterTax();
        if (!priceAfterTax)
            return null;
        return priceAfterTax.multiply(this.getCount());
    }
    getPriceBeforeTax(pricelistId = 0) {
        const taxRate = this.getTaxRate();
        if (!taxRate) {
            return this.getSellingPrice(pricelistId);
        }
        const reverseCalculation = !!Store.get('tenant')?.venue?.settings.reverse_calculation;
        if (reverseCalculation) {
            return this.getSellingPrice(pricelistId);
        }
        return this.getSellingPrice(pricelistId).divide(1 + taxRate.getRate() / 100);
    }
    getPriceAfterTax(pricelistId = 0) {
        const taxRate = this.getTaxRate();
        if (!taxRate) {
            return this.getSellingPrice(pricelistId);
        }
        const reverseCalculation = !!Store.get('tenant')?.venue?.settings.reverse_calculation;
        if (reverseCalculation) {
            return this.getSellingPrice(pricelistId).multiply(1 + taxRate.getRate() / 100);
        }
        return this.getSellingPrice(pricelistId);
    }
    getSellingValueBeforeTax() {
        return this.getPriceBeforeTax().multiply(this.getCount());
    }
    getSellingValueAfterTax() {
        return this.getPriceAfterTax().multiply(this.getCount());
    }
    getNetQuantity() {
        return this.net_quantity ? parseFloat(this.net_quantity) : null;
    }
    getQuantityUnitPrice() {
        const netQuantity = this.getNetQuantity();
        if (!netQuantity)
            return Money.parse(0);
        return this.getPriceAfterTax().divide(netQuantity);
    }
    /**
     * Per-item predicate for typeahead/autocomplete pickers that expose a
     * `filter(item, query)` hook (e.g. cod-typeahead, Vuetify v-autocomplete).
     * Delegates to `search` against a single-element list so the matched fields
     * and matching rules stay identical to the main product search — there is no
     * second copy of the field list to drift out of sync. Quantity and
     * weighted-barcode parsing are disabled (meaningless for a per-item filter).
     * An empty query matches everything.
     */
    static matchesQuery(product, query) {
        if (!query || !query.trim())
            return true;
        return Product.search(query, [product], {
            limit: 1,
            disableQuantity: true,
            disableWeightedBarcodes: true,
        }).products.length > 0;
    }
    static search(query, products = null, options) {
        const { limit = 10, disableQuantity = false, disableWeightedBarcodes = false, disableExactSearch = false } = options;
        const queryText = query?.toLowerCase();
        if (!query) {
            return {
                products: []
            };
        }
        if (!products) {
            products = Product.all().filter(product => product.is_sold);
        }
        // quantity search
        const isQuantitySearch = !disableQuantity ? /^-?[0-9]{1,}\.?[0-9]{0,3}\*/.test(query) : false;
        if (isQuantitySearch) {
            return {
                products: [],
                quantity: parseFloat(query.split('*')[0]),
                query: query.split('*')[1]
            };
        }
        // weighted barcode search
        if (!disableWeightedBarcodes) {
            const barcodePatterns = Store.get('tenant').venue?.settings?.barcode_pattern?.split('|') ?? [];
            for (let i = 0; i < barcodePatterns.length; i++) {
                const barcodePattern = barcodePatterns[i].split(',');
                const minBarcodeLength = barcodePattern[0].length + parseInt(barcodePattern[1]) + parseInt(barcodePattern[2]);
                if (queryText.startsWith(barcodePattern[0]) && queryText.length >= minBarcodeLength) {
                    const codeInBarcode = queryText.substr(barcodePattern[0].length, parseInt(barcodePattern[1]));
                    const product = products.find(product => {
                        const code = product.code ? product.code.toLowerCase().replace(/^0+/, '') : '';
                        return code && code === codeInBarcode.replace(/^0+/, '');
                    });
                    if (product) {
                        const quantityDecimals = !isNaN(+barcodePattern[3]) ? parseInt(barcodePattern[3]) : 3;
                        const quantity = parseFloat(queryText.substr(barcodePattern[0].length + parseInt(barcodePattern[1]), parseInt(barcodePattern[2]))) / Math.pow(10, quantityDecimals);
                        return {
                            products: [product],
                            quantity: quantity
                        };
                    }
                }
            }
        }
        if (!disableExactSearch) {
            // A whole name is not narrowed to its product: it leads the list below, followed by
            // every other product whose name contains it ("Hleb" also lists "Domaći hleb").
            // exact search by barcode
            let productsMatched = products.filter(product => product.barcode && product.barcode.toLowerCase() === queryText);
            if (productsMatched.length) {
                return {
                    products: productsMatched.splice(0, limit)
                };
            }
            // exact search by code
            productsMatched = products.filter(product => product.code && product.code.toLowerCase().replace(/^0+/, '') === queryText.replace(/^0+/, ''));
            if (productsMatched.length) {
                return {
                    products: productsMatched.splice(0, limit)
                };
            }
            // exact search by gtin
            productsMatched = products.filter(product => product.gtin && product.gtin.toLowerCase() === queryText);
            if (productsMatched.length) {
                return {
                    products: productsMatched.splice(0, limit)
                };
            }
        }
        const exact = [];
        const partial = [];
        const doesMatch = (product) => {
            const name = product.name ? product.name.toLowerCase() : '';
            const code = product.code ? product.code.toLowerCase() : '';
            const barcode = product.barcode ? product.barcode.toLowerCase() : '';
            const gtin = product.gtin ? product.gtin.toLowerCase() : '';
            const isExact = name === queryText
                || code === queryText
                || barcode === queryText
                || gtin === queryText;
            const isPartial = name.indexOf(queryText) > -1
                || code.indexOf(queryText) > -1
                || barcode.indexOf(queryText) > -1
                || gtin.indexOf(queryText) > -1;
            return { exact: isExact, partial: isPartial };
        };
        for (const product of products) {
            if (exact.length >= limit)
                break;
            const match = doesMatch(product);
            if (match.exact) {
                exact.push(product);
            }
        }
        for (const product of products) {
            if (exact.length + partial.length >= limit)
                break;
            const match = doesMatch(product);
            if (match.partial && !match.exact) {
                partial.push(product);
            }
        }
        return {
            products: [...exact, ...partial]
        };
    }
    /**
     * Get last update of products from server
     */
    static getLastUpdatedAt() {
        return Product.all().length
            ? moment(Math.max(...Product.all().filter(p => p.updated_at).map(p => moment(p.updated_at).valueOf())))
            : null;
    }
    getUnitOfMeasure() {
        if (!this.unit_of_measure)
            return null;
        const translationKey = `products.unit_of_measure.${this.unit_of_measure}`;
        const translatedValue = I18n.t(translationKey);
        return translatedValue !== translationKey ? translatedValue : this.unit_of_measure;
    }
    getUnitOfMeasureSold() {
        if (!this.unit_of_measure_sold)
            return null;
        const translationKey = `products.unit_of_measure.${this.unit_of_measure_sold}`;
        const translatedValue = I18n.t(translationKey);
        return translatedValue !== translationKey ? translatedValue : this.unit_of_measure_sold;
    }
    usesInventoryAccounting() {
        if (!this.is_goods || this.is_made || !this.is_sold)
            return false;
        return !!Store.get('tenant').venue?.settings?.use_inventory_accounting;
    }
}
export default Product;
modelRegistry.register('Product', Product);
//# sourceMappingURL=Product.js.map